import { useEffect, useMemo, useState } from 'react';
import { ChartType, type TChartType } from '@dbl/core/chart';

interface IUseChartTypeCacheSignatureParams {
  enabled: boolean;
  filtersArr: any[];
  table: any;
  chartData: any;
  displayChartType: TChartType;
}

interface IUseChartTypeCacheSignatureResult {
  filterSignature: string;
  cacheSignatureMap: Record<string, string>;
  updateCacheSignature: (type: TChartType, signature: string) => void;
  updateCacheSignatureByFilters: (type: TChartType, nextFilters?: any[]) => void;
}

// 缓存签名当前仅依赖 filtersArr，保证“筛选变更触发请求、筛选不变复用缓存”。
const buildFilterSignature = (nextFilters: any[] = []): string =>
  JSON.stringify(nextFilters || []);

const useChartTypeCacheSignature = ({
  enabled,
  filtersArr,
  table,
  chartData,
  displayChartType,
}: IUseChartTypeCacheSignatureParams): IUseChartTypeCacheSignatureResult => {
  // 当前筛选条件签名（供图表切换命中缓存判断）
  const filterSignature = useMemo(
    () => (enabled ? buildFilterSignature(filtersArr) : ''),
    [enabled, filtersArr],
  );
  // 按图表类型维护签名：table/line/bar 各自独立缓存
  const [cacheSignatureMap, setCacheSignatureMap] = useState<Record<string, string>>({});

  // 通用更新：仅在签名变化时写入，避免重复 setState。
  const updateCacheSignature = (type: TChartType, signature: string): void => {
    if (!enabled) {
      return;
    }
    setCacheSignatureMap((prev) => {
      if (prev[type] === signature) {
        return prev;
      }
      return {
        ...prev,
        [type]: signature,
      };
    });
  };

  const updateCacheSignatureByFilters = (
    type: TChartType,
    nextFilters: any[] = [],
  ): void => {
    // 外部（如 VS 回调）可直接用新 filters 生成同口径签名并写入。
    updateCacheSignature(type, buildFilterSignature(nextFilters));
  };

  // 统一缓存入库：计算按钮请求成功后也会把当前图表数据写入缓存签名。
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const hasDataByType: Record<string, boolean> = {
      [ChartType.Table]: !!table?.data?.length,
      [ChartType.Line]: !!chartData?.lineChart?.seriesData?.length,
      [ChartType.Bar]: !!chartData?.barChart?.seriesData?.length,
    };
    if (!hasDataByType[displayChartType]) {
      return;
    }
    updateCacheSignature(displayChartType, filterSignature);
  }, [
    enabled,
    chartData?.barChart?.seriesData?.length,
    chartData?.lineChart?.seriesData?.length,
    displayChartType,
    filterSignature,
    table?.data?.length,
  ]);

  return {
    filterSignature,
    cacheSignatureMap,
    updateCacheSignature,
    updateCacheSignatureByFilters,
  };
};

export default useChartTypeCacheSignature;

